import type { CatalogueCheckOutcome, CatalogueStatus, HeldCatalogue } from './catalogue.ts';
import type { ItemSales, ItemSalesPrintOutcome, ItemSalesView } from './item-sales.ts';
import type { PaymentSales } from './payment-sales.ts';
import type {
  LocalPrinterList,
  PrintDocument,
  PrinterSaveOutcome,
  PrinterSetupState,
  PrinterStatus,
  PrinterTestOutcome,
} from './printing.ts';
import type {
  ActiveShift,
  RosterFreshness,
  RosterRefreshOutcome,
  ShiftStartOutcome,
  ShiftStartRequest,
} from './roster.ts';
import type {
  OutletJoinOutcome,
  RecentSales,
  SaleRecordOutcome,
  SaleRecordRequest,
  ShiftTakings,
} from './sales.ts';
import type { SequenceRecoveryRecord } from './sequences.ts';
import type { HeldStock, StockRefreshOutcome, StockStatus } from './stock.ts';
import type {
  SyncBlockedEntry,
  SyncDrainResult,
  SyncEnqueueOutcome,
  SyncEnqueueRequest,
  SyncRequeueOutcome,
  SyncStatus,
} from './sync.ts';
import type {
  HeldRegistration,
  OutletNames,
  TerminalActivationOutcome,
  TerminalIdentity,
  TerminalRegistration,
  TerminalRegistrationOutcome,
} from './terminal.ts';
import type {
  TicketBasketOutcome,
  TicketBasketRequest,
  TicketIssueOutcome,
  TicketIssueRequest,
} from './ticketing.ts';

/**
 * The contract between the preload script and the renderer.
 *
 * **A workspace package as of FE026**, which is when the renderer started
 * *making* writes rather than counting refused ones. It lived in
 * `apps/desktop/src/shared` while only the main process and the preload script
 * read it, and `apps/web` held a hand-copied slice of the two types it needed to
 * count quarantined writes. A till issuing tickets needs the catalogue, the
 * shift, the device's identity and the ticket contract itself, and a second copy
 * of those — carrying an amount in paise and a client-minted id — is a drift
 * hazard on a money path rather than a convenience. Both sides now compile
 * against one definition.
 */

export type DesktopPlatform = 'win32' | 'darwin' | 'linux';

export interface DesktopPrinter {
  print(document: PrintDocument): Promise<void>;
  openCashDrawer(): Promise<void>;
  status(): Promise<PrinterStatus>;
  /** The printer set on this till, and where output goes (#261). */
  setup(): Promise<PrinterSetupState>;
  /**
   * Set this till's printer: a share name, or a network printer's `tcp://`
   * address. Kept on the device and used from the next print on.
   */
  save(setting: string): Promise<PrinterSaveOutcome>;
  /** Print a test page through the printer a ticket would go to. Never rejects on a printer fault. */
  testPage(): Promise<PrinterTestOutcome>;
  /** The printers Windows has on this PC, and which are shared (#282). Never rejects on a listing fault. */
  printers(): Promise<LocalPrinterList>;
}

/** This till's next version (#288). Fetched and installed by the main process; nothing here acts. */
export interface DesktopUpdates {
  /** The version downloaded and waiting to install when the till is closed, or `null`. */
  waiting(): Promise<string | null>;
}

export interface DesktopSync {
  /**
   * Durably queue a write. Resolves once it is on disk, not once it is sent.
   *
   * The request names no actor and carries no credential. Who made the write is
   * stamped in the main process from the shift running at this till, and the
   * lease is attached when the entry is drained — neither is the renderer's to
   * supply, and neither is reachable from here.
   */
  enqueue(request: SyncEnqueueRequest): Promise<SyncEnqueueOutcome>;
  /** Across every registration's queue: they all drain, and they are all this device's. */
  status(): Promise<SyncStatus>;
  /** The writes the API refused, for somebody in the back-office, from every registration. */
  blocked(): Promise<SyncBlockedEntry[]>;
  /**
   * Offer one quarantined write to the queue again.
   *
   * **The id, the registration whose queue it is in, and nothing else.** Each
   * registration keeps its own queue (#243), so the id alone names a row in
   * each; the pair names one. The stored bytes go back under the original
   * key — the renderer supplies no payload, no key and no destination, because
   * a caller that could name any of those could turn a safe replay into a
   * second sale. The API's fingerprint is taken over the request it received,
   * so a changed value under a key it has seen is `SYNC_IDEMPOTENCY_KEY_REUSED`:
   * a non-retryable 409, and permanent quarantine for what is really a
   * duplicate.
   *
   * Worth pressing once whatever the server objected to has been put right. A
   * write it will refuse for ever is refused again and goes back where it was,
   * which costs one attempt and no correctness.
   */
  requeue(id: number, registration: number): Promise<SyncRequeueOutcome>;
  /** Drain immediately rather than waiting for the next tick. */
  drainNow(): Promise<SyncDrainResult>;
}

export interface DesktopTerminal {
  /**
   * Hand the main process a registration an owner has just performed.
   *
   * The renderer is the courier — it made the API call, so the lease arrives in
   * its hands — and its only job is to pass this straight through. It must not
   * keep it, put it in a store, or hold it in a component. What comes back is
   * an outcome, so a refusal can be worded rather than thrown.
   */
  register(
    registration: TerminalRegistration,
    outletNames?: OutletNames,
  ): Promise<TerminalRegistrationOutcome>;
  /**
   * The registration the counter is selling under — whether there is one, where
   * it stands and until when.
   */
  identity(): Promise<TerminalIdentity>;
  /**
   * Every registration this device holds, one per outlet (#243), in the order
   * they were added. Empty on a device never registered.
   */
  registrations(): Promise<HeldRegistration[]>;
  /**
   * Sell under another of this device's registrations from now on.
   *
   * **Carries whoever is signed in across (#254)**, decided in the main process
   * against the roster of the outlet chosen and never by the screen: somebody
   * that roster does not have is refused, and the counter stays where it was.
   * The shift at the outlet left stays open, to be picked up on the way back.
   * With nobody signed in, nobody is — a shift left open at the outlet chosen
   * waits for its holder's PIN.
   */
  activate(id: number): Promise<TerminalActivationOutcome>;
}

export interface DesktopShift {
  /**
   * Check a staff code and PIN against the cached roster, and start a shift.
   *
   * The PIN goes down and an outcome comes back. It is verified in the main
   * process against a verifier the renderer never sees, because a renderer that
   * could read the verifiers could take them off the device — and for a 4–6
   * digit PIN, a verifier is a few minutes of offline guessing away from the
   * PIN itself.
   */
  start(request: ShiftStartRequest): Promise<ShiftStartOutcome>;
  /** Who is at the till. */
  current(): Promise<ActiveShift | null>;
  end(): Promise<void>;
  /** How old the cached roster is. Not who is on it — that is not the renderer's to know. */
  roster(): Promise<RosterFreshness>;
  /** Refresh now rather than waiting for the next tick. */
  refreshRoster(): Promise<RosterRefreshOutcome>;
}

export interface DesktopCatalogue {
  /**
   * The whole sellable set this device holds, or `null` if it has never fetched
   * one.
   *
   * **Wider than `shift.roster()` deliberately, and the departure is worth
   * stating.** The roster returns a freshness projection and never its members,
   * because a verifier that reaches the renderer is a verifier that can leave
   * the device. A catalogue is the opposite: it is what is printed on the
   * buttons and read out at the counter, and the till cannot sell without it.
   * So the items cross, and what still does not cross is the lease — there is no
   * way from here to ask for another outlet's catalogue, because there is no
   * outlet parameter to ask with. (`DesktopSales.joinOutlet` hands back what
   * another registration this device holds may sell into a sale at the
   * counter, from that registration's own copy — #284.)
   *
   * Read from the held copy with no round trip. The version poll in the main
   * process is what keeps it current; a screen must not fetch on mount.
   */
  held(): Promise<HeldCatalogue | null>;
  /** How old the copy is, whether it has been confirmed, and what has happened. */
  status(): Promise<CatalogueStatus>;
  /** Check the version now rather than waiting for the next tick. */
  check(): Promise<CatalogueCheckOutcome>;
}

/**
 * What this device settled about its counters — and nothing it can ask for.
 *
 * There is deliberately no control here. Seeding a counter is not a decision a
 * person standing at a till is equipped to make: it turns on whether a local
 * counter row exists, which the main process reads exactly, and getting it wrong
 * reprints numbers that are already on paper somebody is holding. So the only
 * thing this offers is the answer to "why does this terminal's numbering start
 * where it does".
 */
export interface DesktopSequences {
  /**
   * What this device settled about the terminal it currently is, or `null` if it
   * never has.
   *
   * `null` is the ordinary answer for a till that has always had its own
   * counters — most tills, most days. A record whose `counters` are empty is a
   * terminal the server had never heard a number from, which legitimately starts
   * at the beginning; that is a resolution and not a failure.
   */
  recovery(): Promise<SequenceRecoveryRecord | null>;
}

export interface DesktopStock {
  /**
   * The position this device was last pushed, or `null` if it has never
   * received one.
   *
   * Read from the held copy with no round trip, exactly like the catalogue: the
   * refresh in the main process is what keeps it current, and a screen must not
   * fetch on mount. **Wider than `shift.roster()` and for the catalogue's
   * reason** — a shelf count is not a secret, it is the thing a metre from the
   * screen — and what still does not cross is the lease, because there is no
   * outlet parameter to ask another outlet's position with.
   *
   * **`null` is an ordinary answer and never a reason to stop selling.** A till
   * that has never been pushed a position sells exactly as it did before this
   * existed.
   */
  held(): Promise<HeldStock | null>;
  /** How old the copy is, how many items it covers, and what has happened. */
  status(): Promise<StockStatus>;
  /**
   * Ask for a fresh position now rather than waiting for the next tick.
   *
   * **This is what makes the warning lead somewhere.** A pushed figure is stale
   * by construction, so the answer to "the records say none left" is "the
   * records are forty minutes old" — and somebody has to be able to do something
   * about that from where they are standing. It is the only control this slice
   * offers, and it commits nothing.
   */
  refresh(): Promise<StockRefreshOutcome>;
}

export interface DesktopTickets {
  /**
   * Sell one admission: allocate this terminal's next number, queue the write,
   * and print the artefact.
   *
   * **One call, because the three are one act.** Splitting it would let a
   * renderer allocate a number and not print it, or print and not queue, and the
   * only way to be sure a printed number reaches the server is for nothing
   * between them to be the renderer's to sequence.
   *
   * Resolves once the sale is durably on disk, not once it reaches the API —
   * that is what keeps the gate selling through an outage. **There is no reprint
   * call and there must never be one.**
   */
  issue(request: TicketIssueRequest): Promise<TicketIssueOutcome>;

  /**
   * Sell a party: every admission in the basket, under one answer to how they
   * paid, each one allocated, queued and printed exactly as `issue` does it —
   * one after another, with no tap per person.
   *
   * **Idempotent on `basketId`.** An admission already recorded under it is
   * returned rather than sold again, so pressing again after a failure part-way
   * through sells only what is missing. It stops at the first admission it
   * cannot record and says which ones those are; it does not reject once
   * anything has been spent.
   */
  issueBasket(request: TicketBasketRequest): Promise<TicketBasketOutcome>;
}

export interface DesktopSales {
  /**
   * Ring up one basket: price every line from the held catalogue, allocate this
   * terminal's next receipt number, queue the write, and print the receipt.
   *
   * **One call, because the four are one act** — the same argument
   * `DesktopTickets.issue` makes. Splitting it would let a renderer allocate a
   * number and not print it, or print and not queue, and the only way to be sure
   * a printed number reaches the server is for nothing between them to be the
   * renderer's to sequence. It is also what keeps the arithmetic in one place:
   * the server redoes it and refuses `SALES_PRICE_MISMATCH` or
   * `SALES_TOTAL_MISMATCH` if it disagrees, so the amounts that are printed and
   * the amounts that are sent have to be the same amounts rather than two
   * computations that ought to match.
   *
   * Resolves once the sale is durably on disk, not once it reaches the API. That
   * is what keeps the outlets selling through an outage, and there is nothing
   * here that behaves differently offline: a card sale with the line down is an
   * ordinary sale, because this system records payments rather than processing
   * them.
   */
  record(request: SaleRecordRequest): Promise<SaleRecordOutcome>;

  /**
   * What the running shift has taken, or `null` when nobody is at the till.
   *
   * **Read from this device's own log, and answered offline like everything
   * else here.** It is what lets a drawer be counted at the end of a shift by
   * the person who filled it, rather than by somebody in the back office the
   * next day who was not there.
   *
   * It is not a reconciliation and must not be drawn as one: the server owns
   * the business day and its own totals, and the two disagreeing is the
   * ordinary state of a till that has been offline. See the note above
   * {@link LoggedSale}.
   */
  shift(): Promise<ShiftTakings | null>;

  /**
   * What this till rang up today, newest first.
   *
   * Read-only, and since #294 read for its own sake: what a cashier has taken
   * this shift and what the till has taken today. A refund or a reprint of any
   * of it is a manager's, in the back office.
   *
   * Capped at {@link RECENT_SALES_MAX} with the true count alongside, so a
   * screen can say what it is not showing.
   */
  recent(): Promise<RecentSales>;

  /**
   * What this till sold today, item by item, per outlet (#314) — the same
   * window {@link recent} reads, for the cashier's shift and the whole till.
   * Read-only, offline, from the device's own journal.
   */
  itemSales(): Promise<ItemSales>;

  /**
   * Print {@link itemSales} on this till's printer, one slip per outlet. The
   * main process reads the figures and the park's header itself; the renderer
   * names only which view.
   */
  printItemSales(view: ItemSalesView): Promise<ItemSalesPrintOutcome>;

  /**
   * What this till took today, payment mode by payment mode, per outlet (#323)
   * — the windows and the journal {@link itemSales} reads, so for one window
   * the two totals agree.
   */
  paymentSales(): Promise<PaymentSales>;

  /** Print {@link paymentSales}, one slip per outlet, as {@link printItemSales} does. */
  printPaymentSales(view: ItemSalesView): Promise<ItemSalesPrintOutcome>;

  /**
   * Bring another of this device's outlets into the sale at the counter
   * (#284), **without moving the counter**: the person signed in here is
   * admitted at that registration from its own roster, exactly as a switch
   * would admit them (#254), and its taxed admission comes back to be rung up.
   *
   * Asked when the cashier reaches for that outlet, so one they may not sell at
   * is refused by name then — not at Take payment with the money in a hand.
   */
  joinOutlet(registration: number): Promise<OutletJoinOutcome>;

  /**
   * {@link record}, into another of this device's registrations (#284): its
   * catalogue, its counters, its queue, its paper, drained under its lease.
   * The outlet is still never named on the wire — it is which registration's
   * services the sale is written into, and that lease is what the API reads.
   *
   * A method of its own rather than an argument on `record`, deliberately: a
   * shell one release behind this bundle would ignore a second argument and
   * record the sale **at the outlet the counter is on**. A missing method is
   * detected and the basket is never offered; an ignored argument is a sale
   * booked to the wrong outlet with nothing on screen to say so.
   *
   * Refused `SALE_NO_ACTING_SHIFT` unless the person at the counter is the one
   * selling there, which is what {@link joinOutlet} arranges.
   */
  recordAt(registration: number, request: SaleRecordRequest): Promise<SaleRecordOutcome>;
}

export interface DesktopBridge {
  /**
   * Always true when present. The renderer feature-detects by checking whether
   * the bridge exists at all — in a browser it does not.
   */
  readonly isDesktop: true;
  readonly platform: DesktopPlatform;
  getAppVersion(): Promise<string>;
  readonly printer: DesktopPrinter;
  readonly updates: DesktopUpdates;
  readonly sync: DesktopSync;
  /**
   * The device's own identity. Status only — there is deliberately no way to
   * read the lease from here, because a renderer that can read it is a renderer
   * that can leak it.
   */
  readonly terminal: DesktopTerminal;
  /**
   * Who is working at this device, and the roster that decides it.
   *
   * Separate from `terminal` because they answer different questions: that one
   * is the device's own identity, this one is the person standing at it. The
   * roster's material — verifiers, salts, cost parameters — crosses neither.
   */
  readonly shift: DesktopShift;
  /**
   * What this outlet sells, held so the till keeps selling through an outage.
   *
   * Separate from `shift` because they answer different questions and have
   * different lifetimes: the roster decides who may work here and is refreshed
   * on a schedule, while the catalogue is replaced only when the API says its
   * version has moved.
   */
  readonly catalogue: DesktopCatalogue;
  /**
   * What the records say is on the shelves here, as at one instant.
   *
   * Separate from `catalogue` because they answer different questions and are
   * kept current by different means: the catalogue is what this outlet *sells*
   * and is replaced only when a version counter moves, while this is what it
   * *holds* and has no version to poll — a level is a signed sum over movements
   * with no row to stamp a counter onto, so `asOf` is its only provenance and a
   * cadence is the only way to keep it.
   *
   * **It gates nothing.** CL-801: the till warns and never refuses. A shell that
   * cannot answer this loses a warning rather than a counter.
   */
  readonly stock: DesktopStock;
  /**
   * Where this terminal's own counters stand, and whether that had to be
   * recovered from the server.
   *
   * **Nothing here is part of ordinary selling.** The till is the authority for
   * its own numbers and knows its position; this slice exists for the one case
   * where it does not — a device re-imaged, replaced or restored, which comes
   * back with no counter and would otherwise restart at 1 and collide with every
   * number the server already holds for it.
   *
   * Read-only by design. The recovery is automatic because it turns on a fact
   * the main process can read exactly and a person cannot improve on; what
   * crosses is the account of what was done, so a counter that jumped overnight
   * does not read as a fault.
   */
  readonly sequences: DesktopSequences;
  /**
   * Selling admission, which is the one thing this device does that takes money.
   *
   * Separate from `sync` because a sale is not merely a queued write: it spends
   * a number off this terminal's own counter and puts an artefact in somebody's
   * hand, neither of which the outbox knows anything about.
   */
  readonly tickets: DesktopTickets;
  /**
   * Selling goods over a counter, which is the other thing this device does that
   * takes money.
   *
   * Separate from `tickets` because a basket is not a ticket: it spends a number
   * off a *different* counter, carries one or more lines against one payment,
   * and produces a receipt rather than an artefact that admits somebody. The two
   * share the inversion and nothing else, and merging them would mean one shape
   * carrying a quantity that admission never has and a print path that has to
   * ask which of the two it is drawing.
   */
  readonly sales: DesktopSales;
}

/** Property name the bridge is exposed under on `window`. */
export const DESKTOP_BRIDGE_KEY = 'blossomDesktop';

export const IPC_CHANNELS = {
  appVersion: 'app:version',
  updateWaiting: 'update:waiting',
  printerPrint: 'printer:print',
  printerOpenDrawer: 'printer:open-drawer',
  printerStatus: 'printer:status',
  printerSetup: 'printer:setup',
  printerSave: 'printer:save',
  printerTestPage: 'printer:test-page',
  printerList: 'printer:list',
  syncEnqueue: 'sync:enqueue',
  syncStatus: 'sync:status',
  syncBlocked: 'sync:blocked',
  syncRequeue: 'sync:requeue',
  syncDrain: 'sync:drain',
  terminalRegister: 'terminal:register',
  terminalIdentity: 'terminal:identity',
  terminalRegistrations: 'terminal:registrations',
  terminalActivate: 'terminal:activate',
  shiftStart: 'shift:start',
  shiftCurrent: 'shift:current',
  shiftEnd: 'shift:end',
  rosterStatus: 'roster:status',
  rosterRefresh: 'roster:refresh',
  catalogueHeld: 'catalogue:held',
  catalogueStatus: 'catalogue:status',
  catalogueCheck: 'catalogue:check',
  stockHeld: 'stock:held',
  stockStatus: 'stock:status',
  stockRefresh: 'stock:refresh',
  sequenceRecovery: 'sequences:recovery',
  ticketIssue: 'ticket:issue',
  ticketIssueBasket: 'ticket:issue-basket',
  saleRecord: 'sale:record',
  saleJoinOutlet: 'sale:join-outlet',
  saleRecordAt: 'sale:record-at',
  saleShift: 'sale:shift',
  saleRecent: 'sale:recent',
  saleItems: 'sale:items',
  salePrintItems: 'sale:print-items',
  salePayments: 'sale:payments',
  salePrintPayments: 'sale:print-payments',
} as const;
